import {makeAutoObservable} from 'mobx'
import {CSSProperties} from 'react'
import {DataObjectId, Tag} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {
  minContainerWidth,
  OneLineHeight,
  PanelPaddingX,
  StickersGapX,
  StickersGapY,
  StickersPaddingY
} from '@components/sticker/sticker-panel/containers'
import {StickerPanelProps} from '@components/sticker/sticker-panel/sticker-panel'
import {stickerPanelGlobalStore} from '@components/sticker/sticker-panel/sticker-panel-global-store'
import {StickerPanelContainers} from '@components/sticker/sticker-panel/types'
import {createUseStore} from '@containers/hooks'
import {BoardViewType} from '@extensions/app-persistence'
import {PageService} from '@extensions/page-service'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {Sticker} from '@extensions/sticker/sticker'
import {StickerFilter} from '@extensions/sticker/sticker-filter'

export type StickerPanelLocation = 'board' | 'report' | 'calendar' | 'analytics'

interface StickerPanelState {
  isMultiline: boolean
  stickerTagIdList: DataObjectId[]
  stickersAreaWidth?: number
  stickersMaxHeight?: number
  hiddenStickersAmount?: number
  view?: BoardViewType
  location?: StickerPanelLocation
  filtersAmount: number
  stickersWithFilters: DataObjectId[]
  filtersInHiddenArea: number
  isEditAvailable: boolean
}

export class StickerPanelStore {
  emulated?: StickerPanelProps['emulated']
  filtersController: StickerFilter

  state: StickerPanelState = {
    // expanded: false,
    isMultiline: false,
    stickerTagIdList: [],
    filtersAmount: 0,
    stickersWithFilters: [],
    filtersInHiddenArea: 0,
    isEditAvailable: false
  }

  constructor(view?: BoardViewType, props?: StickerPanelProps) {
    this.emulated = props?.emulated

    this.syncFiltersController()
    this.setView(view)
    this.setLocation(props?.location)
    this.syncStickers()
    this.syncIsEditAvailable()

    makeAutoObservable(this, {}, {autoBind: true})
  }

  syncIsEditAvailable(): void {
    const board = PageService.getCurrentBoard()
    this.state.isEditAvailable =
      this.state.view === 'board' &&
      PermissionsChecker.checkEditBoardStickers(board)
  }

  syncStickers(): void {
    this.syncPanelStickers()
    this.syncFiltersCount()
  }

  syncFiltersController(): void {
    if (this.emulated) {
      this.filtersController = this.emulated.filter
      return
    }

    const currentBoard = PageService.getCurrentBoard()
    this.filtersController = BoardFilters.stickerFilterFromBoard(currentBoard)
  }

  setView(view?: BoardViewType): void {
    this.state.view = view
  }

  setLocation(location?: StickerPanelLocation): void {
    this.state.location = location
  }

  syncPanelStickers(): void {
    if (this.emulated) {
      return
    }

    const board = PageService.getCurrentBoard()
    const stickerTags = Sticker.list(board)

    this.state.stickerTagIdList = stickerTags
      .filter(
        s =>
          !s.deleted &&
          (this.state.view === 'calendar' ? s.title === 'User' : true)
      )
      .map(sticker => sticker.id)
  }

  syncFiltersCount(): void {
    const stickersWithFilters: DataObjectId[] = []

    this.state.filtersAmount = this.state.stickerTagIdList.reduce(
      (acc, stickerId) => {
        const stickerFilter = this.filtersController.getChosen(stickerId)
        if (stickerFilter.length) {
          stickersWithFilters.push(stickerId)
          return acc + 1
        }
        return acc
      },
      0
    )

    this.state.stickersWithFilters = stickersWithFilters
  }

  syncFiltersInHiddenArea(): void {
    const visibleAmountStickers =
      this.state.stickerTagIdList.length -
      (this.state.hiddenStickersAmount || 0)

    this.state.filtersInHiddenArea = this.state.stickerTagIdList.reduce(
      (acc, stickerId, index) => {
        const stickerFilter = this.filtersController.getChosen(stickerId)

        return (
          acc + (index > visibleAmountStickers - 1 ? stickerFilter.length : 0)
        )
      },
      0
    )
  }

  recountPanelSizes(
    {panelWrapper, leftArea, rightArea}: StickerPanelContainers,
    stickersCollection: HTMLCollection
  ): void {
    const currentContainerWidth =
      panelWrapper.offsetWidth -
      leftArea.offsetWidth -
      rightArea.offsetWidth -
      PanelPaddingX * 2
    const containerWidth =
      currentContainerWidth < minContainerWidth
        ? minContainerWidth
        : currentContainerWidth
    const stickersElements = Array.from(stickersCollection) as HTMLDivElement[]

    let firstLineWidth = 0
    let firstLineItems = 0
    let linesAmount = 1

    for (const stickerDiv of stickersElements) {
      if (firstLineWidth >= containerWidth) {
        break
      }
      const elementWidth = Math.ceil(stickerDiv.getBoundingClientRect().width)

      const tempSum = firstLineWidth + elementWidth + StickersGapX
      if (tempSum >= containerWidth) {
        break
      }

      firstLineWidth = tempSum
      firstLineItems += 1
    }
    this.state.stickersAreaWidth = firstLineWidth

    if (firstLineItems !== stickersElements.length) {
      let tempSum = 0
      linesAmount += 1
      const restElements = stickersElements.slice(firstLineItems)
      restElements.forEach(stickerDiv => {
        const currentStickerWidth = Math.ceil(
          stickerDiv.getBoundingClientRect().width
        )
        if (tempSum + currentStickerWidth >= firstLineWidth) {
          tempSum = currentStickerWidth
          linesAmount += 1
        } else {
          tempSum += currentStickerWidth + StickersGapX
        }

        if (
          !stickerPanelGlobalStore.expanded &&
          stickerDiv.firstElementChild?.getAttribute('data-menu-is-opened') ===
            'true'
        ) {
          stickerPanelGlobalStore.setExpanded(true)
        }
      })
    }

    this.state.stickersMaxHeight =
      linesAmount * OneLineHeight +
      StickersPaddingY * 2 +
      (linesAmount - 1) * StickersGapY
    stickerPanelGlobalStore.setOneLineMaxHeight(this.state.stickersMaxHeight)
    this.state.isMultiline = firstLineItems < stickersElements.length
    this.state.hiddenStickersAmount = this.state.isMultiline
      ? stickersElements.length -
        firstLineItems -
        (stickerPanelGlobalStore.expanded && this.state.filtersAmount ? 1 : 0)
      : undefined
  }

  resetFilters(): void {
    this.state.stickersWithFilters.forEach(stickerId => {
      this.filtersController.setFilter(stickerId, [])
    })
    this.state.stickersWithFilters = []
  }

  get stickersList(): Tag[] {
    if (this.emulated) {
      return this.emulated.stickers
    }

    const board = PageService.getCurrentBoard()

    return this.state.stickerTagIdList.map(stickerId =>
      Sticker.getById(board, stickerId)
    )
  }

  get stickerPanelStyles(): CSSProperties {
    const {stickersAreaWidth, stickersMaxHeight} = this.state

    return {
      width: stickersAreaWidth ? `${stickersAreaWidth}px` : 'auto',
      height:
        stickersMaxHeight && stickerPanelGlobalStore.expanded
          ? `${stickersMaxHeight}px`
          : `${OneLineHeight + StickersPaddingY * 2}px`,
      rowGap: `${StickersGapY}px`,
      columnGap: StickersGapX
    }
  }
}

const [StickerPanelContext, useStickerPanelStore] =
  createUseStore(StickerPanelStore)
export {StickerPanelContext, useStickerPanelStore}
